import { Link } from "react-router-dom";
import Subtitle from "../typography/Subtitle";
import Title from "../typography/Title";
import Description from "../typography/Description";
import {
  CodeIcon,
  GraduationCapIcon,
  UsersThreeIcon,
  HandshakeIcon,
  RobotIcon,
  ArrowRightIcon,
} from "@phosphor-icons/react";

const useCases = [
  {
    title: "Technical AI Recruiter",
    description:
      "Screen developers with AI-powered coding tests, technical interviews, and skill-based assessments — find the best engineering talent in hours, not weeks.",
    to: "/use-cases/technical-ai-recruiter",
    icon: CodeIcon,
    accent: "#00E5FF",
    tag: "Engineering Hiring",
  },
  {
    title: "AI Campus Drives",
    description:
      "Run large-scale campus hiring with automated screening, assessments, and interview scheduling — process thousands of graduates simultaneously.",
    to: "/use-cases/ai-campus-drives",
    icon: GraduationCapIcon,
    accent: "#7AA7FF",
    tag: "Campus Recruitment",
  },
  {
    title: "High Volume Recruiter",
    description:
      "Handle thousands of applications simultaneously with AI-driven shortlisting and outreach.",
    to: "/use-cases/high-volume-recruiter",
    icon: UsersThreeIcon,
    accent: "#FF6A00",
    tag: "Scale Hiring",
  },
  {
    title: "Diversity Hiring",
    description:
      "Remove bias from your hiring pipeline with anonymized, skill-first AI evaluations.",
    to: "/use-cases/diversity-hiring",
    icon: HandshakeIcon,
    accent: "#C77DFF",
    tag: "Inclusive Hiring",
  },
  {
    title: "Universal AI Recruiter",
    description:
      "An all-in-one AI recruitment agent for any role, industry, or hiring volume.",
    to: "/use-cases/universal-ai-recruiter",
    icon: RobotIcon,
    accent: "#14AE5C",
    tag: "All-in-One",
  },
];

const UseCases = () => {
  return (
    <section className="relative flex w-screen snap-start snap-always flex-col items-center overflow-hidden bg-gray-900 px-4 py-12 md:py-16">
      <Subtitle>Use Cases</Subtitle>
      <Title as="h2" className="mt-4 max-w-screen-md">
        Built for Every Hiring Challenge
      </Title>
      <Description className="mt-4 max-w-screen-sm">
        Whether you're hiring 10 engineers or 10,000 freshers — there's an AI
        recruiter built for your exact workflow.
      </Description>

      {/* Bento grid: 2 featured cards on top, 3 cards below */}
      <div className="mx-auto mt-12 grid w-full max-w-screen-lg grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {useCases.map((uc, i) => {
          const isFeatured = i < 2;
          return (
            <Link
              key={uc.to}
              to={uc.to}
              className={`group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-white/[0.08] p-6 transition-all duration-300 hover:border-white/[0.16] hover:shadow-2xl ${
                isFeatured
                  ? "lg:col-span-3 lg:min-h-[260px] lg:p-8"
                  : "lg:col-span-2 lg:min-h-[220px]"
              }`}
              style={{
                background: `linear-gradient(135deg, ${uc.accent}08 0%, transparent 60%)`,
              }}
            >
              {/* Hover glow */}
              <div
                className="pointer-events-none absolute -right-20 -top-20 h-40 w-40 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
                style={{ backgroundColor: `${uc.accent}20` }}
              />

              <div className="relative z-10">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex items-center justify-center rounded-xl ${
                      isFeatured ? "h-12 w-12" : "h-10 w-10"
                    }`}
                    style={{ backgroundColor: `${uc.accent}15` }}
                  >
                    <uc.icon
                      size={isFeatured ? 26 : 22}
                      weight="duotone"
                      style={{ color: uc.accent }}
                    />
                  </div>
                  <span
                    className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
                    style={{
                      color: uc.accent,
                      backgroundColor: `${uc.accent}12`,
                    }}
                  >
                    {uc.tag}
                  </span>
                </div>

                <h3
                  className={`mt-4 font-serif font-semibold text-white ${
                    isFeatured ? "text-xl" : "text-base"
                  }`}
                >
                  {uc.title}
                </h3>
                <p
                  className={`mt-2 font-sans leading-relaxed text-gray-400 ${
                    isFeatured ? "text-sm" : "text-[13px]"
                  }`}
                >
                  {uc.description}
                </p>
              </div>

              <span
                className="relative z-10 mt-5 inline-flex items-center gap-1.5 font-serif text-sm font-medium transition-colors"
                style={{ color: uc.accent }}
              >
                Learn more
                <ArrowRightIcon
                  size={14}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default UseCases;
